import { createAsyncThunk } from '@reduxjs/toolkit'
import padStart from 'lodash/padStart'

import { reduxDependenciesType } from '../create'

import config from 'admin/config/main'
import { State } from 'admin/redux/ducks'
import {
  StatisticsType,
  StatisticItemType,
  TransactionsListType,
  TransactionSummaryType,
} from 'admin/types'
import { TransactionsParamsType } from 'admin/types/api'
import createApiReducer, {
  requestDataFor,
  receiveDataFor,
  handleErrorFor,
} from 'redux/utils/createApiDuck'
import dateUtil from 'services/dateUtil'
import StorageHelper from 'services/storageHelper'
import { APIState, ErrorType } from 'types'


const moduleName = `${config.appName  }/dashboard`
const submoduleNames = {
  activePurchasersCount: `${moduleName  }/activePurchasersCount`,
  invoiceCount: `${moduleName  }/invoiceCount`,
  transactionStats: `${moduleName  }/transactionStats`,
}

const activePurchasersCountReducer = createApiReducer(
  submoduleNames.activePurchasersCount,
)
const invoiceCountReducer = createApiReducer(submoduleNames.invoiceCount)
const transactionStatsReducer = createApiReducer(
  submoduleNames.transactionStats,
)

export const fetchActivePurchasersCount = createAsyncThunk<
  number,
  string,
  { state: State; extra: reduxDependenciesType }
>(
  `${submoduleNames.activePurchasersCount}/fetch`,
  async (orgAccountId, { dispatch, extra: { api } }) => {
    dispatch(requestDataFor(submoduleNames.activePurchasersCount)())
    try {
      const activeAccountCount = await api.getActivePurchasersCount(
        orgAccountId
      )
      dispatch(
        receiveDataFor(submoduleNames.activePurchasersCount)({
          activeAccountCount,
        })
      )
      return activeAccountCount
    } catch (error) {
      dispatch(handleErrorFor(submoduleNames.activePurchasersCount)(error))
      return Promise.reject(error)
    }
  }
)

export const fetchTransactionStats = createAsyncThunk<
  TransactionStatsState,
  string,
  { state: State; extra: reduxDependenciesType }
>(
  `${submoduleNames.transactionStats}/fetch`,
  async (orgAccountId, { dispatch, extra: { api } }) => {
    const transactionStatsStorageKey = 'inkop.transactionStats'
    dispatch(requestDataFor(submoduleNames.transactionStats)())
    const storage = new StorageHelper(window.localStorage)
    const transactionStatsCache = storage.getItem(transactionStatsStorageKey)

    // Use cached stats if they exist and aren't stale
    if (
      transactionStatsCache &&
      orgAccountId in transactionStatsCache &&
      'expires' in transactionStatsCache[orgAccountId] &&
      transactionStatsCache[orgAccountId].expires >= Date.now() &&
      transactionStatsCache[orgAccountId].transactionStats
    ) {
      dispatch(
        receiveDataFor(submoduleNames.transactionStats)(
          transactionStatsCache[orgAccountId].transactionStats
        )
      )
      return transactionStatsCache[orgAccountId].transactionStats
    }

    const params: TransactionsParamsType = {
      offset: 0,
      limit: 1000,
      filters: {
        'filter-trans-from': dateUtil
          .getSweDateTimeFromMillis(
            dateUtil.getSweDateTimeNow().toMillis() - 365 * 24 * 60 * 60 * 1000
          )
          .toString(),
        'filter-trans-to': dateUtil.getSweDateNow().toString(),
      },
      sort: 'timestamp',
    }

    let result: TransactionsListType | ErrorType
    let transactions: TransactionSummaryType[] = []

    try {
      do {
        result = await api.getTransactions(orgAccountId, params)
        if (isTransactionType(result)) {
          transactions = transactions.concat(result.transactions)
          params.offset += params.limit
        }
      } while (isTransactionType(result) && result.transactions.length > 0)
    } catch (error) {
      dispatch(handleErrorFor(submoduleNames.transactionStats)(error))
      return Promise.reject(error)
    }

    const transactionStats = {
      transactionMonthStats:
        transactions.length > 0
          ? buildMonthlyTransactionStats(transactions)
          : [],
      transactionRecipientStats:
        transactions.length > 0
          ? buildRecipientTransactionStats(transactions)
          : [],
    }

    dispatch(receiveDataFor(submoduleNames.transactionStats)(transactionStats))

    storage.setItem(transactionStatsStorageKey, {
      ...transactionStatsCache,
      [orgAccountId]: {
        transactionStats,
        expires: dateUtil.getSweDateNow().toMillis() + 24 * 60 * 60 * 1000,
      },
    })

    return transactionStats
  }
)

function isTransactionType(
  transaction: TransactionsListType | ErrorType,
): transaction is TransactionsListType {
  return (transaction as TransactionsListType).transactions !== undefined
}

/**
 * Get monthly aggregate total payment stats
 */
export function buildMonthlyTransactionStats(
  transactions: TransactionSummaryType[],
  months: number = 12,
): StatisticsType {
  const itemMap: { [key: string]: StatisticItemType } = {}
  const datetimeLimit =
    dateUtil.getSweDateTimeNow().toMillis() - months * 31 * 24 * 60 * 60 * 1000
  for (const transaction of transactions) {
    const date = dateUtil.getSweDateTimeFromISO(transaction.transactionTs)

    if (date.toMillis() < datetimeLimit) continue
    const month = `${date.year  }-${  padStart(date.month.toString(), 2, '0')}`
    if (month in itemMap) {
      itemMap[month].value += parseFloat(transaction.totalPaymentAmount)
    } else {
      itemMap[month] = {
        id: month,
        label:
          `${date.year 
          }-${ 
          padStart(date.month.toString(), 2, '0') 
          }-${ 
          padStart(date.day.toString(), 2, '0')}`,
        value: parseFloat(transaction.totalPaymentAmount),
      }
    }
  }
  const items: StatisticItemType[] = Object.values(itemMap)
    .slice(0, months - 1)
    .sort((a, b) => (a.id > b.id ? 1 : -1))
  return {
    count: items.length,
    sum: items.reduce((sum, curr) => sum + curr.value, 0),
    max: items.reduce((max, curr) => (curr.value > max ? curr.value : max), 0),
    avg:
      items.length > 0
        ? items.reduce((sum, curr) => sum + curr.value, 0) / items.length
        : 0,
    last: items.length > 0 ? items.slice(-1).pop().value : null,
    items,
  } as StatisticsType
}

/**
 * Build total payment stats per recipient
 */
function buildRecipientTransactionStats(
  transactions: TransactionSummaryType[],
): StatisticsType {
  const itemMap: { [key: string]: StatisticItemType } = {}
  for (const transaction of transactions) {
    if (transaction.invoiceRecipientId in itemMap) {
      itemMap[transaction.invoiceRecipientId].value += parseFloat(
        transaction.totalPaymentAmount,
      )
    } else {
      itemMap[transaction.invoiceRecipientId] = {
        id: transaction.invoiceRecipientId,
        label: transaction.invoiceRecipientName,
        value: parseFloat(transaction.totalPaymentAmount),
      }
    }
  }
  const items: StatisticItemType[] = Object.values(itemMap).sort((a, b) =>
    a.value > b.value ? -1 : 1,
  )
  return {
    count: items.length,
    sum: items.reduce((sum, curr) => sum + curr.value, 0),
    items,
  } as StatisticsType
}

interface PaymentPurchasersCountState extends APIState {
  data?: {
    activeAccountCount: number
  }
}
export interface InvoiceCountState extends APIState {
  data?: {
    invoiceCount: {
      collection: number
      invoiced: number
      reminder: number
    }
  }
}
interface TransactionStatsState extends APIState {
  data?: {
    transactionMonthStats: StatisticsType
    transactionRecipientStats: StatisticsType
  }
}
export interface DashboardState extends APIState {
  paymentPurchasers: PaymentPurchasersCountState
  transactionStats: TransactionStatsState
}

export {
  activePurchasersCountReducer,
  invoiceCountReducer,
  transactionStatsReducer,
}
